import React, { useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import { Api } from 'utilities'
import { PageHeader, Button, Text1 } from 'components/shared'
import { IconUserManagement } from 'assets'
import PopupContainer from 'components/common/PopupContainer'
import styles from './SsoAllowedEmailsPage.module.scss'

export interface AllowedSsoEmailItem {
  id: number
  email: string
}

const SsoAllowedEmailsPage: React.FC<RouteComponentProps> = () => {
  const [list, setList] = useState<AllowedSsoEmailItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<AllowedSsoEmailItem | null>(null)
  const [emailInput, setEmailInput] = useState('')
  const [submitLoading, setSubmitLoading] = useState(false)

  const fetchList = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await Api.get<AllowedSsoEmailItem[]>('/sso/allowed-emails', 'userApi')
      setList(Array.isArray(data) ? data : [])
    } catch (e) {
      setError(e?.message || 'Failed to load allowed emails')
      setList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
  }, [])

  const openAddModal = () => {
    setEditingItem(null)
    setEmailInput('')
    setModalOpen(true)
  }

  const openEditModal = (item: AllowedSsoEmailItem) => {
    setEditingItem(item)
    setEmailInput(item.email)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingItem(null)
    setEmailInput('')
  }

  const handleSave = async () => {
    const trimmed = emailInput.trim()
    if (!trimmed) return
    setSubmitLoading(true)
    try {
      if (editingItem) {
        await Api.put(`/sso/allowed-emails/${editingItem.id}`, { email: trimmed }, 'userApi')
      } else {
        await Api.post('/sso/allowed-emails', { email: trimmed }, 'userApi')
      }
      closeModal()
      await fetchList()
    } catch (e) {
      const err = e?.message ? JSON.parse(e.message) : {}
      setError(err?.message || 'Request failed')
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleDelete = async (item: AllowedSsoEmailItem) => {
    if (!window.confirm(`Remove "${item.email}" from allowed SSO emails?`)) return
    try {
      await Api.delete(
        `/sso/allowed-emails?email=${encodeURIComponent(item.email)}`,
        'userApi'
      )
      await fetchList()
    } catch (e) {
      const err = e?.message ? JSON.parse(e.message) : {}
      setError(err?.message || 'Delete failed')
    }
  }

  return (
    <div className={styles.pageContainer}>
      <PageHeader
        navSegments={[{
          label: 'Administration',
          icon: IconUserManagement,
          iconProps: { viewBox: '0 0 22 22' }
        }, {
          label: 'Allowed SSO emails'
        }]}
        buttons={[{
          preset: 'primary',
          children: 'Add email',
          onClick: openAddModal
        }]}
      />
      {error && (
        <div className={styles.error}>
          <Text1>{error}</Text1>
        </div>
      )}
      <div className={styles.tableWrap}>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colEmail}>Email</th>
                <th className={styles.colActions}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan={2} className={styles.empty}>
                    No allowed emails yet. Add emails that can sign in with Google or Microsoft on the admin side.
                  </td>
                </tr>
              ) : (
                list.map(item => (
                  <tr key={item.id}>
                    <td className={styles.colEmail}>
                      <Text1 overflowEllipsis title={item.email}>{item.email}</Text1>
                    </td>
                    <td className={styles.colActions}>
                      <Button
                        preset='secondary'
                        onClick={() => openEditModal(item)}
                      >
                        Edit
                      </Button>
                      <Button
                        preset='secondary'
                        onClick={() => handleDelete(item)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <PopupContainer
          title={editingItem ? 'Edit allowed email' : 'Add allowed email'}
          onClose={closeModal}
          size='small'
        >
          <div className={styles.modalContent}>
            <label className={styles.label}>Email</label>
            <input
              type='email'
              className={styles.input}
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder='user@example.com'
            />
            <div className={styles.modalButtons}>
              <Button preset='secondary' onClick={closeModal}>
                Cancel
              </Button>
              <Button
                preset='primary'
                onClick={handleSave}
                disabled={!emailInput.trim() || submitLoading}
              >
                {submitLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </PopupContainer>
      )}
    </div>
  )
}

export default SsoAllowedEmailsPage
